"use strict";

(function (win, doc) {
  const isFetchSupported = typeof fetch !== "undefined" && fetch !== null;
  const mascotMetaData = new Map();
  let previewSettings = {};
  let isAiChatEnabled = false;
  let isStateMachineEnabled = false;
  let chatSessionExtras = "";
  let isChatSessionEnded = false;
  let hasDragged = false;
  let mascots;
  let greetMessage;
  let presetQuestionSdk;
  let sdkProps;
  let industry;
  let customerDomain;

  const httpCall = function (url, method, headers, payload, callback) {
    if (isFetchSupported)
      return fetchWrapper(url, method, headers, payload, callback);
    return invokeXHR(url, method, headers, payload, callback);
  };

  const invokeXHR = function (url, method, headers, payload, callback) {
    const xhr = new XMLHttpRequest();
    if (method === "POST") xhr.withCredentials = true; // Allow sending of cookies
    xhr.open(method, url, true); // Async call for both OLD and NEW browsers
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    xhr.onreadystatechange = function () {
      if (this.readyState === XMLHttpRequest.DONE) {
        if (this.status === 200) {
          const responseData = JSON.parse(this.responseText);
          return callback({ status: "success", response: responseData, code: xhr.status });
        } else {
          return callback({ status: "error", response: "XHR request failed", code: xhr.status });
        }
      }
    };

    xhr.send(payload);
  };

  const fetchWrapper = function (url, method, headers, payload, callback) {
    const config = {
      method: method,
      cache: "reload", // Ensure bypassing the cache
      headers: headers,
      body: payload,
    };
    if (method === "POST") config.credentials = "include";
    fetch(url, config)
      .then(async (res) => {
        const statusCode = res.status;
        let responseData = {};
        try {
          responseData = await res.json();
        } catch (error) {
          responseData = { error: "Failed to parse response" };
        }
        if (statusCode === 200) {
          return callback({ status: "success", response: responseData, code: statusCode });
        } else {
          return callback({ status: "error", response: responseData, code: statusCode });
        }
      })
      .catch((error) => {
        return callback({ status: "error", response: { error: "Network error" }, code: 502 });
      });
  };

  const loadCustomScript = function (chateleonContainer, config) {
    (function (win, d, t, s, obj, oe, st) {
      win["chateleonCustomScript"] = obj;
      oe = d.createElement(t);
      st = d.getElementsByTagName("script")[0];
      oe.async = 1;
      oe.src = s;
      oe.id = "chateleon-custom-script";
      st.parentNode.insertBefore(oe, st);
    })(window, document, "script", config.filePath, "chateleonCustom");

    var customScript = document.querySelector("#chateleon-custom-script");
    customScript.addEventListener("load", function () {
      chateleonCustom(chateleonContainer, config);
    });
  };

  const loadScripts = function (dependency, callback) {
    return new Promise((resolve, reject) => {
      // ✅ Check for global objects if expected
      if (dependency.globalNames) {
        for (let name of dependency.globalNames) {
          if (window[name]) {
            if (callback) callback(window[name]);
            return resolve(window[name]);
          }
        }
      }

      if (typeof window.requirejs !== "undefined") {
        console.info("Loading via require js:", dependency.logMsg);

        requirejs.config({
          paths: { [dependency.id]: dependency.url.replace(/\.js$/, '') },
          shim: dependency.globalNames ? {
            [dependency.id]: { exports: dependency.globalNames[0] }
          } : {}
        });

        require([dependency.id], function (module) {
          if (dependency.globalNames) {
            dependency.globalNames.forEach(name => window[name] = module);
          }
          if (callback) callback(module);
          resolve(module);
        }, function (err) {
          console.error(`Failed to load ${dependency.id} via RequireJS`, err);
          reject(err);
        });
      } else {
        console.info("Loading via <script> tag:", dependency.logMsg);
        const script = document.createElement("script");
        script.id = dependency.id;
        script.src = dependency.url;
        script.async = true;

        script.onload = () => {
          if (dependency.globalNames) {
            let found = false;
            for (let name of dependency.globalNames) {
              if (window[name]) {
                found = true;
                if (callback) callback(window[name]);
                return resolve(window[name]);
              }
            }
            if (!found) {
              return reject(new Error(`${dependency.globalNames.join(" or ")} still undefined after loading ${dependency.url}`));
            }
          } else {
            // For cases like dotlottie-player (custom element)
            if (callback) callback();
            return resolve();
          }
        };

        script.onerror = () => {
          console.error(`Failed to load ${dependency.url}`);
          reject(new Error(`Failed to load ${dependency.url}`));
        };

        document.body.appendChild(script);
      }
    });
  };

  const validateDependency = function (dependency, callback) {
    // Check if already loaded
    if (dependency.globalNames) {
      for (let name of dependency.globalNames) {
        if (window[name]) {
          if (callback) callback({ success: true });
          return;
        }
      }
    }

    // Check if script already exists
    if (document.getElementById(dependency.id)) {
      if (callback) callback({ success: true });
      return;
    }
    
    // Try to load the dependency
    const script = document.createElement("script");
    script.id = dependency.id;
    script.src = dependency.url;
    script.async = true;

    script.onload = () => {
      if (dependency.globalNames) {
        let isLoaded = false;
        for (let name of dependency.globalNames) {
          if (window[name]) {
            isLoaded = true;
            break;
          }
        }
        if (callback) callback({ success: isLoaded, error: isLoaded ? null : "Dependency loaded but global not found" });
      } else {
        if (callback) callback({ success: true });
      }
    };

    script.onerror = () => {
      if (callback) callback({ success: false});
    };

    document.body.appendChild(script);
  }

  const loadRiveAnimation = function (responseData, MASCOT_CONTAINER_ID, callback, index, config, resolve) {
    const dependencies = {
      id: "rive-dependency-script",
      url: "https://unpkg.com/@rive-app/canvas@2.7.0/rive.js",
      logMsg: "Riven Dependencies Loaded Successfully",
      globalNames: ["rive"]
    };

    const createRiveElement = function (riveGlobal) {
      let riveCanvas = document.getElementById(`${MASCOT_CONTAINER_ID}-rive-${index}`);
      if (!riveCanvas) {
        riveCanvas = document.createElement("canvas");
        riveCanvas.id = `${MASCOT_CONTAINER_ID}-rive-${index}`;
        riveCanvas.style.display = "none";
        document.body.appendChild(riveCanvas);
      }

      // Set high resolution for the canvas
      const dpr = window.devicePixelRatio || 1;
      const width = parseInt(previewSettings.mascotWidth, 10) || parseInt(config.meta.dimensions.width, 10) || 150;
      const height = parseInt(previewSettings.mascotHeight, 10) || parseInt(config.meta.dimensions.height, 10) || 150;
      riveCanvas.width = width * dpr;
      riveCanvas.height = height * dpr;
      riveCanvas.style.width = `${width}px`;
      riveCanvas.style.height = `${height}px`;

      // ✅ use riveGlobal.Rive
      const riveAnimation = new riveGlobal.Rive({
        src: responseData,
        canvas: riveCanvas,
        autoplay: true,
        onLoad: () => {
          callback(riveCanvas, index, resolve);
        },
        // Enable high-DPI rendering
        useOffscreenRenderer: true
      });
    };

    loadScripts(dependencies, createRiveElement);
  };

  const loadLottieJSONAnimation = function (responseData, MASCOT_CONTAINER_ID, callback, index, resolve) {
    const dependencies = {
      id: "lottie",
      url: "https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.7.13/lottie.js",
      logMsg: "Lottie Json Dependencies Loaded Successfully",
      globalNames: ["lottie"]
    };

    const createLottieElement = function (lottie) {
      const container = document.createElement("div");
      container.id = `${MASCOT_CONTAINER_ID}-lottie-${index}`;
      container.style.display = "none";

      const animation = lottie.loadAnimation({
        container,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: responseData
      });

      animation.addEventListener('DOMLoaded', () => {
        callback(container, index, resolve);
      });
    };

    loadScripts(dependencies, createLottieElement);
  };

  const loadDotLottieAnimation = function (responseData, MASCOT_CONTAINER_ID, callback, index, resolve) {
    const dependencies =
    {
      id: "lottie-dependency-script",
      url: "https://cdn.jsdelivr.net/npm/@dotlottie/player-component/dist/dotlottie-player.js",
      fallbackUrl: [
        "https://unpkg.com/@dotlottie/player-component@1.3.0/dist/dotlottie-player.js"
      ],
      logMsg: "Lottie Dependencies Loaded Successfully",
      globalNames: ["DotLottiePlayer"]
    }

    const createLottieElement = function () {
      const lottieAnimationContainer =
        document.createElement("dotlottie-player");
      lottieAnimationContainer.src = responseData;
      lottieAnimationContainer.id = `${MASCOT_CONTAINER_ID}-lottie-${index}`;
      lottieAnimationContainer.setAttribute("loop", "true");  // Ensures it's recognized as a boolean
      lottieAnimationContainer.setAttribute("autoplay", "true");
      lottieAnimationContainer.style.display = "none";
      callback(lottieAnimationContainer, index, resolve);
    }
    loadScripts(dependencies, createLottieElement);
  }

  const loadModelViewer3DAnimation = function (responseData, MASCOT_CONTAINER_ID, callback, index, resolve) {
    const dependencies =
    {
      id: "model-viewer-dependency-script",
      url: "https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js",
      type: "module",
      logMsg: "Model Viewer Dependencies Loaded Successfully",
      globalNames: ["model-viewer"]
    }

    const createModelViewerElement = function () {
      const modelViewer = document.createElement('model-viewer');
      modelViewer.id = `${MASCOT_CONTAINER_ID}-model-viewer-${index}`;
      modelViewer.src = responseData;
      modelViewer.alt = 'A 3D model of Chateleon mascot';
      modelViewer.setAttribute('ar', false);
      modelViewer.setAttribute('auto-rotate', false);
      modelViewer.setAttribute('camera-controls', true);
      modelViewer.style.display = "none";
      callback(modelViewer, index, resolve);
    }
    loadScripts(dependencies, createModelViewerElement);
  }

  const loadGifAnimation = function (responseData, MASCOT_CONTAINER_ID, callback, index, resolve) {
    const gifAnimationContainer = new Image();
    gifAnimationContainer.src = responseData;
    gifAnimationContainer.id = `${MASCOT_CONTAINER_ID}-gif-${index}`;
    gifAnimationContainer.style.display = "none";
    callback(gifAnimationContainer, index, resolve);
  };

  const detectDeviceType = function () {
    const userAgent = navigator.userAgent.toLowerCase();

    if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
      return "Mobile";
    }
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
      return "Tablet";
    }
    return "Desktop";
  }

  const chateleon = function (...args) {
    let config = {
      apiId: "",
      event: "",
      customScript: args[0].customScript || false,
      filePath: args[0].filePath || undefined,
      position: args[0].position || {
        type: "fixed",
        right: "0%",
        bottom: "0%",
      },
      append: args[0].append || {
        id: undefined,
        class: undefined,
      },
      meta: {
        dimensions: (args[0].meta && args[0].meta.dimensions) || {
          height: "150px",
          width: "150px",
          zIndex: 500,
        },
      },
      formSchema: {
        theme:
          (args[0].formSchema && args[0].formSchema.theme) || "transparent", // Avaliable themes 'dark' & 'light' & 'transparent'.
        dimensions: {
          width: (args[0].formSchema && args[0].formSchema.dimensions?.width) || "420px"
        },
        components: (args[0].formSchema && args[0].formSchema.components) || [
          {
            type: "chat_messages_with_query_input",
            brandName: "",
            placeholder: "Ask me anything...",
            required: true,
            botMessageColor: "#E5FF95",
            userMessageColor: "#BFC9FF",
          },
          {
            type: "powered_by",
            editable: false,
            poweredBy_text: "Powered by",
            website_url_text: "Chateleon.com",
            url: "https://chateleon.com",
          },
        ],
      },
    };
    if (typeof args[0] === "object") {
      config.apiId = args[0].apiId || console.info("Invalid api key");
      config.event = args[0].event || "create";
    } else {
      // On calling sdk from custom script
      config.apiId = args[1] || console.info("Invalid api key");
      config.event = args[0] || "create";
      config.customImpl = args[4] || undefined;
    }

    // BASE_URL should be replaced using a script like github workflow
    const BASE_URL = "https://sdk-api.chateleon.com";
    const CDN_BASE_URL = "https://integration-kit.chateleon.com";
    const SERVE_PATH = "/serve/v2";
    const TRACK_SERVE_PATH = "/track/serve";
    const TRACK_INTERACTION_PATH = "/track/interaction";
    const MASCOT_CONTAINER_ID = config.id
      ? ["chateleon-container", config.id].join("-")
      : "chateleon-container";
    const METHOD = "POST";
    console.info("Chateleon SDK loaded");
    const chateleonContainer = document.createElement("div");
    chateleonContainer.id = MASCOT_CONTAINER_ID;
    switch (config.event) {
      case "move":
        moveGif([config.apiId], MASCOT_CONTAINER_ID);
        break;
      case "create":
        // Do an API call with the key and validate
        var url = BASE_URL + SERVE_PATH;
        var headers = {
          "Content-Type": "application/json",
          "x-api-key": config.apiId,
        };
        var method = METHOD;
        var payload = null;
        // Call to `/serve` API
        httpCall(url, method, headers, payload, serverApiCallbackHandler);
        break;
      default:
        console.warn("Chateleon Warning : Invalid event : " + config.event);
    }

    async function serverApiCallbackHandler(res) {
      var response = res.response;
      if (res.status === "success") {
        // Handle success response
        win.chateleonIsLoaded = true;
        mascots = response.data.gifs;
        const domainData = response.data.domainData || [];
        industry = domainData[0].industry;
        customerDomain = domainData[0].name.split("://")[1].split("/")[0]; // Trimming the domain name from the url.

        domainData.forEach((domain) => {
          isAiChatEnabled = domain.isAiChatEnabled;
          isStateMachineEnabled = domain.isStateMachineEnabled;
          previewSettings = domain.positionData || {};
          greetMessage = domain.greetings || "Hey there! How can I assist you today?";
          presetQuestionSdk = domain.questions || [];
        });

        // Process mascots sequentially
        for (const [index, mascot] of mascots.entries()) {

          // Use a Promise to wait for each rendered mascot to append in DOM
          await new Promise((resolve) => {
            mascotMetaData.set(index, { speed: mascot.speed, screenTime: mascot.screenTime, nextState: mascot.nextStates, eventType: mascot.eventType, bubbleOffset: mascot.bubbleOffset, message: mascot.message });

            switch (mascot.mascotExtension) {
              case "RIVE":
                processRiveAnimation(mascot.original, index, resolve);
                break;
              case "LOTTIE_JSON":
                processLottieJsonAnimation(mascot.original, index, resolve);
                break;
              case "LOTTIE":
                processDotLottieAnimation(mascot.original, index, resolve);
                break;
              case "GLB":
                processModelViewerAnimation(mascot.original, index, resolve);
                break;
              default:
                processGifAnimation(mascot.original, index, resolve);
            }
          });
        }
        trackServe();
      } else {
        // Handle error response
        win.chateleonIsLoaded = false;
        console.error(response);
      }
    }

    function processRiveAnimation(mascot, index, resolve) {
      loadRiveAnimation(mascot, MASCOT_CONTAINER_ID, addStyles, index, config, resolve);
    }

    function processLottieJsonAnimation(mascot, index, resolve) {
      loadLottieJSONAnimation(mascot, MASCOT_CONTAINER_ID, addStyles, index, resolve);
    }

    function processDotLottieAnimation(mascot, index, resolve) {
      loadDotLottieAnimation(mascot, MASCOT_CONTAINER_ID, addStyles, index, resolve);
    }

    function processModelViewerAnimation(mascot, index, resolve) {
      loadModelViewer3DAnimation(mascot, MASCOT_CONTAINER_ID, addStyles, index, resolve);
    }

    function processGifAnimation(mascot, index, resolve) {
      loadGifAnimation(mascot, MASCOT_CONTAINER_ID, addStyles, index, resolve);
    }

    function addStyles(renderedMascot, index, resolve) {
      createAndAppendElementInDOM(renderedMascot, index, resolve);
    }

    function createAndAppendElementInDOM(renderedMascot, index, resolve) {
      renderedMascot.style.cursor = "pointer";
      renderedMascot.style.height = "100%";
      renderedMascot.style.width = "100%";
      chateleonContainer.style.height = previewSettings.mascotHeight || config.meta.dimensions.height || 150 + "px";
      chateleonContainer.style.width = previewSettings.mascotWidth || config.meta.dimensions.width || 150 + "px";
      chateleonContainer.style.position = (previewSettings.mascotLeft || previewSettings.mascotRight) ? "fixed" : config.position.type || "fixed";
      chateleonContainer.style.left = (previewSettings.mascotLeft || previewSettings.mascotRight) ? previewSettings.mascotLeft : config.position.left || null;
      chateleonContainer.style.top = (previewSettings.mascotTop || previewSettings.mascotBottom) ? previewSettings.mascotTop : config.position.top || null;
      chateleonContainer.style.right = (previewSettings.mascotLeft || previewSettings.mascotRight) ? previewSettings.mascotRight : config.position.right || null;
      chateleonContainer.style.bottom = (previewSettings.mascotTop || previewSettings.mascotBottom) ? previewSettings.mascotBottom : config.position.bottom || null;
      chateleonContainer.style.zIndex =
        config.meta.dimensions.zIndex || config.dimensions.zIndex || 500;

      const elementHasId = config.append.id !== undefined;
      appendAnimation(elementHasId, renderedMascot, chateleonContainer, index, resolve);
    }

    async function appendAnimation(elementHasId, renderedMascot, chateleonContainer, index, resolve) {
      let element;
      let touchStartTime = 0;
      let chateleonSubContainer = doc.createElement("div");
      chateleonSubContainer.id = `chateleon-sub-container-${index}`;
      chateleonSubContainer.style.height = "100%";
      chateleonSubContainer.style.width = "100%";
      chateleonSubContainer.style.display = "none";

      // Tabs and Mobiles
      if (window.innerWidth < 991) {
        let height = (!!previewSettings.mascotHeight) ? previewSettings.mascotHeight.split("px")[0] : config.meta.dimensions.height.split("px")[0];
        let width = (!!previewSettings.mascotWidth) ? previewSettings.mascotWidth.split("px")[0] : config.meta.dimensions.width.split("px")[0];
        let heightInt = parseInt(height);
        let widthtInt = parseInt(width);

        // Height
        renderedMascot.style.height = "100%";
        chateleonSubContainer.style.height = "100%";
        chateleonContainer.style.height = heightInt * 0.7 + "px" || 150 + "px";
        // Width
        renderedMascot.style.height = "100%";
        chateleonSubContainer.style.height = "100%";
        chateleonContainer.style.width = widthtInt * 0.7 + "px" || 150 + "px";
      }

      chateleonSubContainer.appendChild(renderedMascot);
      mascotMetaData.get(index).mascot = chateleonSubContainer;
      renderedMascot.style.display = "block";
      chateleonContainer.appendChild(chateleonSubContainer);

      if ((mascots.length - (index) === mascots.length)) {
        sdkProps = { industry, customerDomain, hasDragged, chatSessionExtras, isChatSessionEnded, previewSettings, greetMessage, presetQuestionSdk, isStateMachineEnabled, isAiChatEnabled, httpCall, detectDeviceType, mascotMetaData, loadScripts, validateDependency };
        chateleonSubContainer.style.display = "block";
        doc.body.appendChild(chateleonContainer);

        if (isAiChatEnabled) { // true or false
          // Hiding the chateleon container
          chateleonContainer.style.display = "none";
          const dependencies =
          {
            id: "chateleon-ai-agent",
            url: CDN_BASE_URL + "/v0/aiAgent.js",
            logMsg: "Ai-Agent Dependencies Loaded Successfully",
            globalNames: ["chateleonLaunchAiAgent"]
          }

          loadScripts(dependencies, () => { chateleonLaunchAiAgent(chateleonContainer, sdkProps, config) })
        } else {
          if (config.customScript) { //true or false
            loadCustomScript(chateleonContainer, config);
          }
        }
        if (config.customImpl && !config.customScript) {
          config.customImpl(chateleonContainer, isAiChatEnabled, null, null);
        }

        //Events
        // Hover event for desktops / laptops
        chateleonContainer.onmouseenter = function (event) {
          trackEvent(event, "mouseover");
        };

        // Hover and Click event for mobiles / tablets
        chateleonContainer.ontouchstart = function () {
          touchStartTime = Date.now();
        };

        chateleonContainer.ontouchend = function (event) {
          let touchDuration = Date.now() - touchStartTime;
          if (touchDuration > 250) {
            trackEvent(event, "mouseover");
          } else {
            trackEvent(event, "mouseover");
            trackEvent(event, "click");
          }
        };

        // Click event for desktops / laptops 
        chateleonContainer.onclick = function (event) {
          trackEvent(event, "click");
        };
      }

      if (mascots.length - 1 === index) {
        if (isStateMachineEnabled) {
          // Initialize the state machine with the globalAnimationObject
          const dependencies =
          {
            id: "chateleon-state-machine",
            url: CDN_BASE_URL + "/v0/stateMachine.js",
            logMsg: "State-Machine Dependencies Loaded Successfully",
            globalNames: ["chateleonStateMachine"]
          }

          loadScripts(dependencies, () => { chateleonStateMachine(mascotMetaData, sdkProps, config) });
        }
      }

      switch (elementHasId) {
        case true:
          element = document.getElementById(config.append.id);
          if (element) {
            element.appendChild(chateleonContainer);
          }
          break;
        case false:
          const elementHasClass = config.append.class !== undefined;
          if (elementHasClass) {
            element = document.querySelector("." + config.append.class);
            if (element) {
              element.appendChild(chateleonContainer);
            }
          }
          break;
      }
      resolve(); // Resoling promise after the rendered animation has been apppended to the DOM
    }

    function serveAndInteractionCallback(res) {
      const response = res.response;
      if (res.status === "error") {
        console.error(res.status + ": " + response);
      }
    }

    function trackServe() {
      var url = BASE_URL + TRACK_SERVE_PATH;
      var headers = {
        "Content-Type": "application/json",
        "x-api-key": config.apiId,
      };
      var method = METHOD;
      var payload = null;
      // Call to `track/serve` API
      httpCall(url, method, headers, payload, serveAndInteractionCallback);
    }

    function trackInteraction(eventData) {
      var url = BASE_URL + TRACK_INTERACTION_PATH;
      var headers = {
        "Content-Type": "application/json",
        "x-api-key": config.apiId,
      };
      var method = METHOD;
      var payload = JSON.stringify({
        eventData: {
          type: eventData.type === 'click' ? 'click' : 'mouseover',
        },
      });
      // Call to `track/interaction` API
      httpCall(url, method, headers, payload, serveAndInteractionCallback);
    }

    function moveGif(data, id) {
      const gifContainer = doc.querySelector("#" + id);
      // TODO: Data validation pending

      // Loop through each property in data
      for (let prop in data) {
        // Check if the property exists in data and is not null or undefined
        if (
          data.hasOwnProperty(prop) &&
          data[prop] !== null &&
          data[prop] !== undefined
        ) {
          gifContainer.style[prop] = data[prop];
        } else {
          gifContainer.style[prop] = null;
        }
      }
    }

    const trackEvent = function (event, interactionType) {
      // Add interaction type to the event object
      const eventData = {
        type: interactionType,
        originalEvent: event,
      };
      // Do an API call and store the tracking information
      trackInteraction(eventData);
    };

    return {
      httpCall: httpCall,
      loadScripts: loadScripts,
      loadCustomScript: loadCustomScript,
      detectDeviceType: detectDeviceType,
      serverApiCallbackHandler: serverApiCallbackHandler,
    }
  };

  win.chateleon = chateleon;
})(window, document);
